import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, CheckCircle2, X as XIcon, RotateCcw, Mail, Phone, MapPin, Flame, Package, Calendar, MessageSquare, RefreshCw, ArrowRight, Archive, Send } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const PACKAGE_LABEL = { guenstig: "Günstig", komfort: "Komfort", premium: "Premium" };

export default function TelesalesDirector() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardMode, setDiscardMode] = useState("archive");
  const [discardReason, setDiscardReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telesales/director/pending-review");
      setLeads(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const releaseLead = async (lead) => {
    if (!window.confirm(`Lead "${lead.first_name} ${lead.last_name}" an den Vertrieb freigeben?`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/telesales/director/leads/${lead.id}/release`);
      toast.success(`Freigegeben → ${data.lead.distribution_mode === "fixed" ? "zugewiesen" : "in Auktion"}`);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  const discardLead = async () => {
    if (!selected) return;
    if (discardMode === "send_back_to_agent" && !discardReason.trim()) {
      toast.error("Bitte einen Grund für die Rückgabe an den Agent angeben");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/telesales/director/leads/${selected.id}/discard`, {
        mode: discardMode,
        reason: discardReason || null,
      });
      toast.success(discardMode === "archive" ? "Archiviert" : "Zurück an Agent");
      setDiscardOpen(false);
      setSelected(null);
      setDiscardReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwerfen fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="telesales-director">
      <AppHeader title="Telesales-Direktor" back="/dashboard" />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Crown className="text-amber-400" size={22} /> Pending-Review Queue
            </h1>
            <p className="text-[12px] text-zinc-300 font-medium">Leads Ihrer Telesales-Agents warten auf Freigabe an den Vertrieb</p>
          </div>
          <button
            onClick={load}
            data-testid="director-refresh"
            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium"
          >
            <RefreshCw size={14} /> Neu laden
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-zinc-500" size={28} /></div>
        ) : leads.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center" data-testid="director-empty">
            <CheckCircle2 className="mx-auto text-[#00E575] mb-3" size={48} />
            <h3 className="font-bold text-lg mb-1">Alles erledigt</h3>
            <p className="text-zinc-300 text-sm font-medium">Keine Leads in der Freigabe-Queue.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="director-leads-list">
            {leads.map((l) => (
              <div
                key={l.id}
                data-testid={`director-lead-${l.id}`}
                onClick={() => setSelected(l)}
                className="bg-[#18181B] border border-zinc-800 hover:border-amber-500/40 rounded-2xl p-4 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[12px] font-bold uppercase tracking-widest">Pending Review</span>
                      <span className="text-[12px] text-zinc-300 font-medium">via {l.agent_name || l.agent_email || "Agent"}</span>
                    </div>
                    <h3 className="font-bold text-[15px] mb-1">
                      {l.salutation ? `${l.salutation} ` : ""}{l.first_name} {l.last_name}
                    </h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-zinc-300 font-medium">
                      {l.phone && <span className="inline-flex items-center gap-1"><Phone size={11} /> {l.phone}</span>}
                      {l.email && <span className="inline-flex items-center gap-1"><Mail size={11} /> {l.email}</span>}
                      {(l.zip || l.city) && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {l.zip} {l.city}</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[12px]">
                      <span className="text-zinc-400"><Flame size={10} className="inline mr-0.5" /> {l.current_heating}</span>
                      <span className="text-zinc-400"><Package size={10} className="inline mr-0.5" /> {PACKAGE_LABEL[l.package] || l.package}</span>
                      <span className="text-zinc-400"><Calendar size={10} className="inline mr-0.5" /> {l.appointment_date} {l.appointment_time}</span>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-zinc-600 mt-1 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Detail panel */}
        {selected && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-3" data-testid="lead-detail-modal">
            <div className="w-full max-w-2xl bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 sticky top-0 bg-[#18181B] z-10">
                <div>
                  <h3 className="font-bold">{selected.first_name} {selected.last_name}</h3>
                  <p className="text-[12px] text-zinc-300 font-medium">via {selected.agent_name || selected.agent_email}</p>
                </div>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-5 space-y-4 text-[13px]">
                <DetailRow label="Telefon" value={selected.phone} icon={Phone} />
                <DetailRow label="E-Mail" value={selected.email} icon={Mail} />
                <DetailRow label="Adresse" value={[selected.street, `${selected.zip || ""} ${selected.city || ""}`.trim()].filter(Boolean).join(", ")} icon={MapPin} />
                <DetailRow label="Aktuelle Heizung" value={selected.current_heating} icon={Flame} />
                <DetailRow label="Verbrauch" value={selected.consumption_value ? `${selected.consumption_value} ${selected.consumption_unit || ""}` : null} />
                <DetailRow label="Wohnfläche" value={selected.living_area_sqm ? `${selected.living_area_sqm} m²` : null} />
                <DetailRow label="Personen" value={selected.occupants} />
                <DetailRow label="Baujahr" value={selected.construction_year} />
                <DetailRow label="Paket-Wunsch" value={PACKAGE_LABEL[selected.package] || selected.package} icon={Package} />
                <DetailRow label="Termin" value={`${selected.appointment_type} – ${selected.appointment_date} ${selected.appointment_time}`} icon={Calendar} />
                <DetailRow label="Notiz Agent" value={selected.agent_note} icon={MessageSquare} />
                {selected.orientation_offer?.estimated_heat_load_kw && (
                  <div className="p-3 rounded-lg bg-[#00E575]/5 border border-[#00E575]/20">
                    <div className="text-[12px] uppercase tracking-widest text-[#00E575] mb-1 font-bold">Orientierungsangebot</div>
                    <div className="text-zinc-200 font-mono text-[12px]">
                      Heizlast {selected.orientation_offer.estimated_heat_load_kw} kW
                      {selected.orientation_offer.package && <> • {selected.orientation_offer.package.name}</>}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-3 border-t border-zinc-800">
                  <button
                    onClick={() => releaseLead(selected)}
                    disabled={submitting}
                    data-testid="director-release"
                    className="flex-1 h-11 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
                  >
                    <Send size={14} /> An Vertrieb freigeben
                  </button>
                  <button
                    onClick={() => setDiscardOpen(true)}
                    data-testid="director-discard"
                    className="flex-1 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 inline-flex items-center justify-center gap-2"
                  >
                    <Archive size={14} /> Verwerfen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discard modal */}
        {discardOpen && selected && (
          <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-3" data-testid="discard-modal">
            <div className="w-full max-w-md bg-[#18181B] border border-zinc-800 rounded-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                <h3 className="font-bold">Lead verwerfen</h3>
                <button onClick={() => setDiscardOpen(false)} className="p-1 hover:bg-zinc-800 rounded">
                  <XIcon size={16} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setDiscardMode("archive")}
                    data-testid="discard-mode-archive"
                    className={`p-3 rounded-lg border text-left ${
                      discardMode === "archive"
                        ? "border-amber-500/40 bg-amber-500/10"
                        : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-[13px]"><Archive size={13} /> Archivieren</div>
                    <div className="text-[10.5px] text-zinc-400 mt-1">Bleibt im „Verworfen"-Tab. Kein Routing.</div>
                  </button>
                  <button
                    onClick={() => setDiscardMode("send_back_to_agent")}
                    data-testid="discard-mode-send-back"
                    className={`p-3 rounded-lg border text-left ${
                      discardMode === "send_back_to_agent"
                        ? "border-blue-500/40 bg-blue-500/10"
                        : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 font-bold text-[13px]"><RotateCcw size={13} /> Zurück an Agent</div>
                    <div className="text-[10.5px] text-zinc-400 mt-1">Agent sieht den Lead mit Kommentar.</div>
                  </button>
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">
                    Grund / Kommentar {discardMode === "send_back_to_agent" && "*"}
                  </label>
                  <textarea
                    rows={3}
                    value={discardReason}
                    onChange={(e) => setDiscardReason(e.target.value)}
                    data-testid="discard-reason"
                    className="w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 focus:border-[#00E575] outline-none text-[13px]"
                  />
                </div>
                <button
                  onClick={discardLead}
                  disabled={submitting}
                  data-testid="discard-submit"
                  className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : "Bestätigen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const DetailRow = ({ label, value, icon: Icon }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex gap-3">
      <div className="w-32 flex-shrink-0 text-[12px] uppercase tracking-widest text-zinc-300 font-bold pt-0.5 flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="flex-1 text-zinc-200">{String(value)}</div>
    </div>
  );
};
